import React from 'react';

import { Text } from '@lightspeed/flame/Text';
import { useQuery } from '@tanstack/react-query';

import { createReactRenderer } from 'helpers/react-renderer';
import { translate } from 'root/apps/pages/utils/translate';
import {
  InvoiceReferenceContainer,
  InvoiceReferenceLoading,
  InvoiceReferenceSpinner,
} from 'root/apps/QuoteMachineIntegration/components/InvoiceReferenceBanner/style';
import InvoiceService from 'root/apps/QuoteMachineIntegration/services/InvoiceService';
import invoiceServiceV1 from 'root/apps/QuoteMachineIntegration/services/v1/InvoiceService';
import { InvoiceReference } from 'root/apps/QuoteMachineIntegration/state';
import Modules from 'shared_modules/Modules';

interface InvoiceReferenceBannerProps {
  saleID: string;
  creditAccountID?: string;
  customerID?: string;
}

const mapV1ToInvoiceReference = (invoice: any): InvoiceReference => {
  const customerName = `${invoice?.customer?.firstName || ''} ${
    invoice?.customer?.lastName || ''
  }`.trim();

  const invoiceNumber = invoice?.identifierNumber || null;

  return {
    identifierNumber: Number(invoiceNumber) || 0,
    proposalURL: invoice?.proposalURL || '',
    customerName: customerName || '',
    sendEmail: invoice?.terms?.sendByEmail ?? false,
    toEmailAddress: invoice?.terms?.customerEmail || '',
    invoiceID: invoice?.id || '',
  };
};

const InvoiceReferenceBanner: React.FC<InvoiceReferenceBannerProps> = ({
  saleID,
  creditAccountID,
  customerID,
}) => {
  const isV1Enabled: boolean = Modules.hasModule('invoicing.api.v1');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['invoiceReference'],
    queryFn: async () => {
      // Call v1 endpoint if module is enabled
      if (isV1Enabled) {
        // Wait 2 seconds before first call to give invoicing service time to index
        await new Promise((resolve) => setTimeout(resolve, 2000));

        const response = await invoiceServiceV1.getInvoiceByTransactionRef(
          String(creditAccountID),
          saleID
        );
        return mapV1ToInvoiceReference(response);
      }

      // Legacy flow
      const response = await InvoiceService.getInvoiceReference(saleID);
      return response;
    },
    retry: false,
    cacheTime: 0,
    refetchOnMount: true,
  });

  if (isLoading) {
    return (
      <InvoiceReferenceLoading alignItems="center" data-test="loadingSpinner">
        <InvoiceReferenceSpinner size="26px" color="#37ab5a" />
        <Text style={{ fontSize: '1rem', padding: 0 }}>
          {translate('Loading invoice details...')}
        </Text>
      </InvoiceReferenceLoading>
    );
  }

  if (isError || !data) {
    return <></>;
  }

  const invoiceSentByEmail = data.sendEmail && data.toEmailAddress !== '';

  // V1 flow: render different text with "invoice" as a link
  if (isV1Enabled && customerID) {
    const invoiceLinkUrl =
      data.identifierNumber > 0
        ? data.proposalURL
        : `/?name=customer.views.customer&form_name=view&id=${customerID}&tab=account`;
    const targetAttr = data.identifierNumber > 0 ? ' target="_blank"' : '';
    const invoiceLinkHtml = `<a href="${invoiceLinkUrl}"${targetAttr} data-test="invoiceLink">${translate(
      'invoice'
    )}</a>`;

    const formattedMessage = invoiceSentByEmail
      ? translate(
          `Sending ${invoiceLinkHtml} to&nbsp;<var class="notranslate" data-test="emailAddress">${data.toEmailAddress}</var>`
        )
      : translate(
          `Saving ${invoiceLinkHtml} to the account of&nbsp;<var data-test="customerName">${data.customerName}</var>`
        );

    return (
      <InvoiceReferenceContainer>
        <Text style={{ fontSize: '1rem' }}>
          {/* eslint-disable-next-line react/no-danger */}
          <span dangerouslySetInnerHTML={{ __html: formattedMessage }} />
        </Text>
      </InvoiceReferenceContainer>
    );
  }
  // Legacy flow
  const invoiceNumberHtml =
    data.proposalURL !== ''
      ? `<var><a href="${data.proposalURL}" target="_blank" data-test="invoiceIdentifierLink">#${data.identifierNumber}</a></var>`
      : '';

  const formattedLegacyMessage = invoiceSentByEmail
    ? translate(
        `Invoice ${invoiceNumberHtml}&nbsp;sent to <var data-test="emailAddress">${data.toEmailAddress}</var>`
      )
    : translate(
        `Invoice ${invoiceNumberHtml}&nbsp;added to the account of <var data-test="customerName">${data.customerName}</var>`
      );

  return (
    <InvoiceReferenceContainer>
      <Text style={{ fontSize: '1rem' }}>
        {/* eslint-disable-next-line react/no-danger */}
        <span dangerouslySetInnerHTML={{ __html: formattedLegacyMessage }} />
      </Text>
    </InvoiceReferenceContainer>
  );
};

const RenderedComponent = createReactRenderer({
  component: InvoiceReferenceBanner,
  selector: '[data-react-root="InvoiceNotificationBanner"]',
});

history.onChangeState(
  { pathname: '/register.php', form_name: undefined, id: undefined, view: undefined },
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);

export default InvoiceReferenceBanner;
